import React, { useState } from 'react';
import {
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Button,
  IconButton,
  MenuItem,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import SectionTitle from './SectionTitle';
import { BusinessTripData } from './types';
import { StyledPanel } from 'src/components/StyledPanel';
import { StyledFlex } from 'src/components/StyledFlex';
import NumberField from 'src/components/NumberField';

const BUSINESS_TRIP_TRANSPORT_TYPES = [
  'car_diesel',
  'car_petrol',
  'car_lpg',
  'car_hybrid',
  'car_electric_vehicle',
  'car_unknown',
  'motorbike',
  'taxi',
  'bus_coach',
  'bus_local',
  'train',
  'flight_short_haul_up_to_4h_economy_class',
  'flight_short_haul_up_to_4h_business_class',
  'flight_long_haul_more_than_4h_economy_class',
  'flight_long_haul_more_than_4h_business_class',
  'flight_long_haul_more_than_4h_first_class',
  'ferry_foot_passenger',
  'ferry_car_passenger',
  'none',
];

interface Props {
  data: BusinessTripData[];
  onChange: (data: BusinessTripData[]) => void;
  onSave: () => Promise<void>;
}

export default function BusinessTripsSection(props: Props) {
  const { t } = useTranslation('legacyGhg', {
    keyPrefix: 'employee-survey',
  });
  const { data, onChange, onSave } = props;
  const [changed, setChanged] = useState(false);

  const updateBusinessTrip = (
    index: number,
    field: keyof BusinessTripData,
    value: string | number | null
  ) => {
    const newData = [...data];
    if (field === 'type_of_trip_transport') {
      newData[index][field] = value as string;
    } else {
      newData[index][field] = value as number | null;
    }
    onChange(newData);
    setChanged(true);
  };

  const addBusinessTrip = () => {
    const newData = [
      ...data,
      {
        type_of_trip_transport: '',
        times_in_period: null,
        average_distance_per_trip_km: null,
      },
    ];
    onChange(newData);
    setChanged(true);
  };

  const handleDeleteClick = (index: number) => {
    const newData = [...data];
    newData.splice(index, 1);
    onChange(newData);
    setChanged(true);
  };

  return (
    <>
      <SectionTitle>{t('question3')}</SectionTitle>
      <StyledPanel>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('preview-typeof-transport')}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('preview-number-of-trips')}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('preview-average_distance_per_trip_km')}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="subtitle2" fontWeight="bold">
                  Actions
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 2 }}
                  >
                    No business trip data available
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.map((trip, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={trip.type_of_trip_transport}
                      onChange={(e) =>
                        updateBusinessTrip(
                          index,
                          'type_of_trip_transport',
                          e.target.value
                        )
                      }
                      placeholder={t('preview-typeof-transport')}
                    >
                      {BUSINESS_TRIP_TRANSPORT_TYPES.map((type) => (
                        <MenuItem key={type} value={type}>
                          {t(type)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <NumberField
                      fullWidth
                      size="small"
                      value={trip.times_in_period ?? ''}
                      onChange={(value) =>
                        updateBusinessTrip(index, 'times_in_period', value)
                      }
                      placeholder={t('preview-number-of-trips')}
                      inputProps={{ min: 0, step: 1 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={trip.average_distance_per_trip_km ?? ''}
                      onChange={(e) =>
                        updateBusinessTrip(
                          index,
                          'average_distance_per_trip_km',
                          e.target.value ? Number(e.target.value) : null
                        )
                      }
                      placeholder={t('preview-average_distance_per_trip_km')}
                      inputProps={{ min: 0, step: 0.1 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(index)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <StyledFlex mt={2} justifyContent="space-between">
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addBusinessTrip}
            size="small"
          >
            {t('add-another')}
          </Button>

          {changed && (
            <Button
              size="small"
              variant="contained"
              onClick={() => onSave().then(() => setChanged(false))}
            >
              {t('save')}
            </Button>
          )}
        </StyledFlex>
      </StyledPanel>
    </>
  );
}
